import { FilterSummary } from './FilterSummary'
import React, { useState } from 'react'
import {
  FREE_TEXT_FILTER_TYPES,
  NodeStatus,
  PathScope,
  ScopeFilterConfiguration,
  ScopeFilterWithStatus,
} from '../../lib/InstallationScope'
import styled from 'styled-components'

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1em;
`

const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
`

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  min-width: 1100px;
  min-height: 600px;

  > * {
    flex: 1 100%;
  }
`

type Props = {
  modifiedScopeFilters?: ScopeFilterWithStatus[]
  onComplete: (filters: ScopeFilterWithStatus[]) => void
}

/**
 * Filters out unnecessary exclude scopes when there are include scopes present. As includes
 * will make the others implicitly excluded. Only applies within the same filter type.
 */
const filterUnnecessaryExcludeScopes = (scopes: ScopeFilterWithStatus[]): ScopeFilterWithStatus[] => {
  const typesWithIncludes = new Set(
    scopes.filter((filter) => filter.status === NodeStatus.INCLUDED).map((filter) => filter.type),
  )

  if (typesWithIncludes.size === 0) {
    return scopes
  }

  return scopes.map((filter) => {
    if (filter.status === NodeStatus.EXCLUDED && typesWithIncludes.has(filter.type)) {
      return { ...filter, status: NodeStatus.NONE as NodeStatus.NONE }
    }
    return filter
  })
}

export const FilterAssignment = ({ modifiedScopeFilters, onComplete }: Props) => {
  const [pathScopes, setPathScopes] = useState<PathScope[]>([])

  const [filterConfiguration, setFilterConfiguration] = useState<ScopeFilterConfiguration>(
    new ScopeFilterConfiguration(modifiedScopeFilters ?? []),
  )

  const applyFilter = (updatedFilter: ScopeFilterWithStatus) => {
    const index = filterConfiguration.scopeFilters.findIndex(
      (filter) => updatedFilter.type === filter.type && updatedFilter.value === filter.value,
    )
    filterConfiguration.scopeFilters = [
      ...filterConfiguration.scopeFilters.slice(0, index),
      updatedFilter,
      ...filterConfiguration.scopeFilters.slice(index + 1),
    ]

    setFilterConfiguration(new ScopeFilterConfiguration(filterConfiguration.scopeFilters))

    if (!filterConfiguration.isScopeFiltersEnabled()) {
      setPathScopes(
        pathScopes.flatMap((pathScope) =>
          pathScope.include.map(() => {
            return {
              include: pathScope.include,
              exclude: [...new Set(pathScope.exclude)],
            }
          }),
        ),
      )
    }
  }

  const applyAddFilter = (newFilter: ScopeFilterWithStatus) => {
    if (!FREE_TEXT_FILTER_TYPES.includes(newFilter.type)) return

    const existingIndex = filterConfiguration.scopeFilters.findIndex(
      (filter) => filter.type === newFilter.type && filter.value === newFilter.value,
    )

    if (existingIndex >= 0) {
      applyFilter(newFilter)
    } else {
      setFilterConfiguration(new ScopeFilterConfiguration([...filterConfiguration.scopeFilters, newFilter]))
    }
  }

  const applyRemoveFilter = (filterToRemove: ScopeFilterWithStatus) => {
    if (!FREE_TEXT_FILTER_TYPES.includes(filterToRemove.type)) return

    const updatedFilters = filterConfiguration.scopeFilters.filter(
      (filter) => !(filter.type === filterToRemove.type && filter.value === filterToRemove.value),
    )
    setFilterConfiguration(new ScopeFilterConfiguration(updatedFilters))
  }

  return (
    <RowContainer>
      <Heading>
        <div className="w-full grid-cols-2">
          <div className="justify-center">
            <h3 className="mt-md">Define filters For Common Assortment</h3>
          </div>
        </div>
        <div className="w-full grid-cols-2">
          <div className="mt-sm float-right">
            <button
              className="btn btn-primary-dark float-right py-sm mr-lg"
              onClick={() => onComplete(filterUnnecessaryExcludeScopes(filterConfiguration.scopeFilters))}
            >
              Save
            </button>
          </div>
        </div>
      </Heading>
      <Container>
        <div className="flex-1">
          {filterConfiguration.scopeFilters.length > 0 || FREE_TEXT_FILTER_TYPES.length > 0 ? (
            <FilterSummary
              filters={filterConfiguration.scopeFilters}
              onChange={applyFilter}
              onAddFilter={applyAddFilter}
              onRemoveFilter={applyRemoveFilter}
            />
          ) : null}
        </div>
      </Container>
    </RowContainer>
  )
}
